function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
import PropTypes from "prop-types";
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { highlight } from 'refractor';
import { keys, useCombinedRefs } from '../../services';
import { OuiButtonIcon } from '../button';
import { keysOf } from '../common';
import { OuiCopy } from '../copy';
import { OuiFocusTrap } from '../focus_trap';
import { OuiI18n } from '../i18n';
import { useInnerText } from '../inner_text';
import { useMutationObserver } from '../observer/mutation_observer';
import { useResizeObserver } from '../observer/resize_observer';
import { OuiOverlayMask } from '../overlay_mask';
var isAstElement = function isAstElement(node) {
  return node.hasOwnProperty('type') && node.type === 'element';
};
var nodeToHtml = function nodeToHtml(node, idx, nodes) {
  var depth = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  if (isAstElement(node)) {
    var properties = node.properties,
      tagName = node.tagName,
      children = node.children;
    return /*#__PURE__*/React.createElement(tagName, _objectSpread(_objectSpread({}, properties), {}, {
      key: "node-".concat(depth, "-").concat(idx),
      className: classNames(properties.className)
    }), children && children.map(function (el, i) {
      return nodeToHtml(el, i, nodes, depth + 1);
    }));
  }
  return node.value;
};
var addLineData = function addLineData(nodes) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    lineNumber: 1
  };
  return nodes.reduce(function (result, node) {
    var lineStart = data.lineNumber;
    if (node.type === 'text') {
      if (!node.value.match(/\r\n?|\n/)) {
        node.lineStart = lineStart;
        node.lineEnd = lineStart;
        result.push(node);
      } else {
        var lines = node.value.split(/\r\n?|\n/);
        lines.forEach(function (line, i) {
          var num = i === 0 ? data.lineNumber : ++data.lineNumber;
          result.push({
            type: 'text',
            value: i === lines.length - 1 ? line : "".concat(line, "\n"),
            lineStart: num,
            lineEnd: num
          });
        });
      }
      return result;
    }
    if (node.children && node.children.length) {
      var _first$lineStart, _last$lineEnd;
      var children = addLineData(node.children, data);
      var first = children[0];
      var last = children[children.length - 1];
      var start = (_first$lineStart = first.lineStart) !== null && _first$lineStart !== void 0 ? _first$lineStart : lineStart;
      var end = (_last$lineEnd = last.lineEnd) !== null && _last$lineEnd !== void 0 ? _last$lineEnd : lineStart;
      if (start !== end) {
        children.forEach(function (node) {
          result.push(node);
        });
      } else {
        node.lineStart = start;
        node.lineEnd = end;
        node.children = children;
        result.push(node);
      }
      return result;
    }
    result.push(node);
    return result;
  }, []);
};
function wrapLines(nodes) {
  var grouped = [];
  nodes.forEach(function (node) {
    var lineStart = node.lineStart - 1;
    if (grouped[lineStart]) {
      grouped[lineStart].push(node);
    } else {
      grouped[lineStart] = [node];
    }
  });
  var wrapped = [];
  grouped.forEach(function (node) {
    wrapped.push({
      type: 'element',
      tagName: 'span',
      properties: {
        className: ['ouiCodeBlock__line']
      },
      children: node
    });
  });
  return wrapped;
}
var highlightByLine = function highlightByLine(children, language) {
  return wrapLines(addLineData(highlight(children, language)));
};
var fontSizeToClassNameMap = {
  s: 'ouiCodeBlock--fontSmall',
  m: 'ouiCodeBlock--fontMedium',
  l: 'ouiCodeBlock--fontLarge'
};
export var FONT_SIZES = keysOf(fontSizeToClassNameMap);
var paddingSizeToClassNameMap = {
  none: '',
  s: 'ouiCodeBlock--paddingSmall',
  m: 'ouiCodeBlock--paddingMedium',
  l: 'ouiCodeBlock--paddingLarge'
};
export var PADDING_SIZES = keysOf(paddingSizeToClassNameMap);
/**
 * This is the base component extended by OuiCode and OuiCodeBlock.
 * These components share the same propTypes definition with OuiCodeBlockImpl.
 */
export var OuiCodeBlockImpl = ({
  transparentBackground = false,
  paddingSize = 'l',
  fontSize = 's',
  isCopyable = false,
  whiteSpace = 'pre-wrap',
  language,
  inline,
  children,
  className,
  overflowHeight,
  ...rest
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [wrapperRef, setWrapperRef] = useState(null);
  const [innerTextRef, _innerText] = useInnerText('');
  const innerText = useMemo(() => _innerText?.replace(/[\r\n?]{2}|\n\n/g, '\n'), [_innerText]);
  const [tabIndex, setTabIndex] = useState(-1);
  const combinedRef = useCombinedRefs([innerTextRef, setWrapperRef]);
  const {
    width,
    height
  } = useResizeObserver(wrapperRef);
  const content = useMemo(() => {
    if (!language || typeof children !== 'string') {
      return children;
    }
    const nodes = inline ? highlight(children, language) : highlightByLine(children, language);
    return nodes.length === 0 ? children : nodes.map(nodeToHtml);
  }, [children, language, inline]);
  const doesOverflow = () => {
    if (!wrapperRef) return;
    const {
      clientWidth,
      clientHeight,
      scrollWidth,
      scrollHeight
    } = wrapperRef;
    const doesOverflow = scrollHeight > clientHeight || scrollWidth > clientWidth;
    setTabIndex(doesOverflow ? 0 : -1);
  };
  useMutationObserver(wrapperRef, doesOverflow, {
    subtree: true,
    childList: true
  });
  useEffect(doesOverflow, [width, height, wrapperRef]);
  const onKeyDown = event => {
    if (event.key === keys.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      closeFullScreen();
    }
  };
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };
  const closeFullScreen = () => {
    setIsFullScreen(false);
  };
  const classes = classNames('ouiCodeBlock', fontSizeToClassNameMap[fontSize], paddingSizeToClassNameMap[paddingSize], {
    'ouiCodeBlock--transparentBackground': transparentBackground,
    'ouiCodeBlock--inline': inline,
    'ouiCodeBlock--hasControls': isCopyable || overflowHeight
  }, {
    prismjs: !className?.includes('prismjs'),
    [`language-${language || 'none'}`]: !className?.includes('language')
  }, className);
  const codeClasses = classNames('ouiCodeBlock__code', language);
  const preClasses = classNames('ouiCodeBlock__pre', {
    'ouiCodeBlock__pre--whiteSpacePre': whiteSpace === 'pre',
    'ouiCodeBlock__pre--whiteSpacePreWrap': whiteSpace === 'pre-wrap'
  });
  const optionalStyles = {};
  if (overflowHeight) {
    optionalStyles.maxHeight = overflowHeight;
  }
  const codeSnippet = <code className={codeClasses} {...rest}>
      {content}
    </code>;
  const wrapperProps = {
    className: classes,
    style: optionalStyles
  };
  if (inline) {
    return <span {...wrapperProps}>{codeSnippet}</span>;
  }
  const getCopyButton = textToCopy => {
    let copyButton;
    if (isCopyable && textToCopy) {
      copyButton = <div className="ouiCodeBlock__copyButton">
          <OuiI18n token="ouiCodeBlock.copyButton" default="Copy">
            {copyButton => <OuiCopy textToCopy={textToCopy}>
                {copy => <OuiButtonIcon onClick={copy} iconType="copy" color="text" aria-label={copyButton} />}
              </OuiCopy>}
          </OuiI18n>
        </div>;
    }
    return copyButton;
  };
  let fullScreenButton;
  if (!inline && overflowHeight) {
    fullScreenButton = <OuiI18n tokens={['ouiCodeBlock.fullscreenCollapse', 'ouiCodeBlock.fullscreenExpand']} defaults={['Collapse', 'Expand']}>
        {([fullscreenCollapse, fullscreenExpand]) => <OuiButtonIcon className="ouiCodeBlock__fullScreenButton" onClick={toggleFullScreen} iconType={isFullScreen ? 'cross' : 'fullScreen'} color="text" aria-label={isFullScreen ? fullscreenCollapse : fullscreenExpand} />}
      </OuiI18n>;
  }
  const getCodeBlockControls = textToCopy => {
    let codeBlockControls;
    const copyButton = getCopyButton(textToCopy);
    if (copyButton || fullScreenButton) {
      codeBlockControls = <div className="ouiCodeBlock__controls">
          {fullScreenButton}
          {copyButton}
        </div>;
    }
    return codeBlockControls;
  };
  const getFullScreenDisplay = codeBlockControls => {
    let fullScreenDisplay;
    if (isFullScreen) {
      // Force fullscreen to use large font and padding.
      const fullScreenClasses = classNames('ouiCodeBlock', fontSizeToClassNameMap[fontSize], 'ouiCodeBlock-paddingLarge', 'ouiCodeBlock-isFullScreen', className);

      /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
      fullScreenDisplay = <OuiOverlayMask>
          <OuiFocusTrap clickOutsideDisables={true}>
            <div className={fullScreenClasses}>
              <pre className={preClasses} tabIndex={0}>
                <code className={codeClasses} onKeyDown={onKeyDown}>
                  {content}
                </code>
              </pre>

              {codeBlockControls}
            </div>
          </OuiFocusTrap>
        </OuiOverlayMask>;
      /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
    }
    return fullScreenDisplay;
  };
  const codeBlockControls = getCodeBlockControls(innerText);
  return <div {...wrapperProps}>
      <pre ref={combinedRef} style={optionalStyles} className={preClasses} tabIndex={tabIndex}>
        {codeSnippet}
      </pre>
      {/*
          If the below fullScreen code renders, it actually attaches to the body because of
          OuiOverlayMask's React portal usage.
        */}
      {codeBlockControls}
      {getFullScreenDisplay(codeBlockControls)}
    </div>;
};
OuiCodeBlockImpl.propTypes = {
  className: PropTypes.string,
  fontSize: PropTypes.oneOf(["s", "m", "l"]),
  /**
     * Displays the passed code in an inline format. Also removes any margins set.
     */
  inline: PropTypes.bool,
  /**
     * Displays an icon button to copy the code snippet to the clipboard.
     */
  isCopyable: PropTypes.bool,
  /**
     * Sets the syntax highlighting for a specific language
     * @see https://github.com/wooorm/refractor#syntaxes
     * for options
     */
  language: PropTypes.string,
  overflowHeight: PropTypes.number,
  paddingSize: PropTypes.oneOf(["none", "s", "m", "l"]),
  transparentBackground: PropTypes.bool,
  /**
     * Specify how `white-space` inside the element is handled.
     * `pre` respects line breaks/white space but doesn't force them to wrap the line
     * `pre-wrap` respects line breaks/white space but does force them to wrap the line when necessary.
     */
  whiteSpace: PropTypes.oneOf(["pre", "pre-wrap"])
};
try {
  OuiCodeBlockImpl.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3077135706/src/src/components/code/_code_block.tsx',
    description: 'This is the base component extended by OuiCode and OuiCodeBlock.\n' + 'These components share the same propTypes definition with OuiCodeBlockImpl.',
    displayName: 'OuiCodeBlockImpl',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      fontSize: {
        defaultValue: {
          value: 's'
        },
        description: '',
        name: 'fontSize',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'FontSize',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      inline: {
        defaultValue: null,
        description: 'Displays the passed code in an inline format. Also removes any margins set.',
        name: 'inline',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isCopyable: {
        defaultValue: {
          value: 'false'
        },
        description: 'Displays an icon button to copy the code snippet to the clipboard.',
        name: 'isCopyable',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      language: {
        defaultValue: null,
        description: 'Sets the syntax highlighting for a specific language\n' + '@see https://github.com/wooorm/refractor#syntaxes\n' + 'for options',
        name: 'language',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      overflowHeight: {
        defaultValue: null,
        description: '',
        name: 'overflowHeight',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      paddingSize: {
        defaultValue: {
          value: 'l'
        },
        description: '',
        name: 'paddingSize',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'PaddingSize',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"none"'
          }]
        }
      },
      transparentBackground: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'transparentBackground',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      whiteSpace: {
        defaultValue: {
          value: 'pre-wrap'
        },
        description: 'Specify how `white-space` inside the element is handled.\n' + "`pre` respects line breaks/white space but doesn't force them to wrap the line\n" + '`pre-wrap` respects line breaks/white space but does force them to wrap the line when necessary.',
        name: 'whiteSpace',
        parent: {
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        },
        declarations: [{
          fileName: 'src/src/components/code/_code_block.tsx',
          name: 'OuiCodeBlockImplProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"pre" | "pre-wrap"',
          value: [{
            value: '"pre"'
          }, {
            value: '"pre-wrap"'
          }]
        }
      }
    },
    extendedInterfaces: ['OuiCodeBlockImplProps']
  };
} catch (e) {}